import { memo } from './memo';

/**
 * Checks whether the given argument is a valid URL
 *
 * @param url URL to be validated
 * */
export function isValidUrl(url: string): boolean {
  try {
    // eslint-disable-next-line no-new
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Sanitizes a URL by removing directory traversal attempts and ensuring it starts with a slash.
 *
 * This function is memoized for performance optimization.
 *
 * @example
 * ```ts
 * const sanitizedUrl = sanitizeUrl('http://example.com/../path/to/resource');
 * console.log(sanitizedUrl); // Output: '/path/to/resource'
 * ```
 *
 * @param str The URL string to sanitize.
 *
 * @returns The sanitized URL string.
 */
export const sanitizeUrl = memo(
  (str: string) => {
    let url = str.trim();

    // Step 1: Limit input length
    const MAX_URL_LENGTH = 2000;

    if (url.length > MAX_URL_LENGTH) {
      url = url.slice(0, MAX_URL_LENGTH);
    }

    // Step 2: Decode the URL to handle encoded traversal attempts
    let decodedUrl;

    try {
      decodedUrl = decodeURIComponent(url);
    } catch (e) {
      // If decoding fails, use the original URL
      decodedUrl = url;
    }

    // Step 3: Remove directory traversal attempts
    const parts = decodedUrl.split('/');
    const sanitizedParts = [];

    for (const part of parts) {
      if (part === '.' || part === '..') {
        continue; // Skip . and .. parts
      }

      sanitizedParts.push(part);
    }

    // Step 4: Reconstruct the URL
    let sanitizedUrl = sanitizedParts.join('/');

    // Step 5: Remove trailing slashes without regex
    while (sanitizedUrl.endsWith('/')) {
      sanitizedUrl = sanitizedUrl.slice(0, -1);
    }

    // Step 6: Ensure the URL starts with a slash if it's not empty
    if (sanitizedUrl && !sanitizedUrl.startsWith('/')) {
      sanitizedUrl = `/${sanitizedUrl}`;
    }

    sanitizedUrl = sanitizedUrl.replace(/^\/+/, '/');

    return sanitizedUrl;
  },
  { maxSize: 32 },
);
